import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import AgentStatusIndicator from '../../../components/AgentStatusIndicator';
import Button from '../../../components/ui/Button';

const AgentCard = ({ 
  agent,
  locked = false,
  requiredPlan = null,
  onUpgrade,
  className = '',
  subscription,
  agentQuantity = 0,
  onQuantityChange,
  totalAgents = 0,
  maxTotalAgents = 2
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (locked && onUpgrade) {
      onUpgrade();
      return;
    }
    if (agent?.route) {
      navigate(agent?.route);
    }
  };

  const getMaxPerAgent = () => {
    // All tiers can now set up to 5 agents per individual agent type
    return 5;
  };

  const getTotalMaxAgents = () => {
    const tier = subscription?.subscription_plans?.tier || 'free';
    if (tier === 'enterprise') return 25;
    if (tier === 'draftiq_plus') return 4;
    return 2; // free
  };

  const handleIncrement = (e) => {
    e?.stopPropagation();
    if (locked && onUpgrade) {
      onUpgrade();
      return;
    }
    const maxPerAgent = getMaxPerAgent();
    // Check both per-agent limit AND total limit
    if (agentQuantity < maxPerAgent && totalAgents < maxTotalAgents && onQuantityChange) {
      onQuantityChange(agent?.id, agentQuantity + 1);
    }
  };

  const handleDecrement = (e) => {
    e?.stopPropagation();
    if (agentQuantity > 0 && onQuantityChange) {
      onQuantityChange(agent?.id, agentQuantity - 1);
    }
  };

  const getGradientClass = (color) => {
    const gradients = {
      purple: 'from-purple-500/20 to-purple-600/10',
      blue: 'from-blue-500/20 to-blue-600/10',
      emerald: 'from-emerald-500/20 to-emerald-600/10',
      amber: 'from-amber-500/20 to-amber-600/10',
      rose: 'from-rose-500/20 to-rose-600/10',
      orange: 'from-orange-500/20 to-orange-600/10'
    };
    return gradients?.[color] || gradients?.purple;
  };

  return (
    <div
      onClick={handleCardClick}
      className={`
        relative elevated-card rounded-2xl border border-border/50
        transition-smooth hover-lift cursor-pointer group
        backdrop-blur-sm bg-gradient-to-br from-card/95 to-card/80
        ${
          locked ? 'opacity-70' : ''
        }
      `}
      role="button"
      tabIndex={0}
      aria-label={`View ${agent?.name} details`}
      onKeyDown={(e) => {
        if (e?.key === 'Enter' || e?.key === ' ') {
          e?.preventDefault();
          handleCardClick();
        }
      }}
    >
      {locked && (
        <div className="absolute inset-0 glass-strong backdrop-blur-md rounded-2xl flex items-center justify-center z-10">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-muted/50 to-muted/30 flex items-center justify-center">
              <Icon name="Lock" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-base font-bold text-foreground mb-2">Upgrade to Unlock</p>
            {requiredPlan && (
              <p className="text-sm text-muted-foreground mb-4">
                Requires {requiredPlan} plan or higher
              </p>
            )}
            <Button variant="default" size="sm" iconName="ArrowRight" iconPosition="right" className="shadow-lg">
              View Plans
            </Button>
          </div>
        </div>
      )}
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div 
              className={`
                flex-shrink-0 w-14 h-14 rounded-xl 
                bg-gradient-to-br ${getGradientClass(agent?.color)}
                flex items-center justify-center shadow-lg
                group-hover:scale-110 transition-transform duration-300
              `}
            >
              <Icon 
                name={agent?.icon} 
                size={28} 
                color={`var(--color-${agent?.color || 'primary'})`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-foreground mb-2 font-heading">
                {agent?.name}
              </h3>
              <AgentStatusIndicator 
                status={agent?.status}
                agentName={agent?.name}
                size="default"
                showLabel={true}
              />
            </div>
          </div>
        </div>

        {/* Quantity Controls */}
        {!locked && onQuantityChange && (
          <div className="mb-6">
            <div className="glass-subtle rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Quantity</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDecrement}
                    disabled={agentQuantity === 0}
                    className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                    aria-label="Decrease quantity"
                  >
                    <Icon name="Minus" size={16} className="text-foreground" />
                  </button>
                  <span className="text-lg font-bold text-foreground min-w-[2rem] text-center">
                    {agentQuantity}
                  </span>
                  <button
                    onClick={handleIncrement}
                    disabled={agentQuantity >= getMaxPerAgent() || totalAgents >= maxTotalAgents}
                    className="w-8 h-8 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                    aria-label="Increase quantity"
                  >
                    <Icon name="Plus" size={16} className="text-primary-foreground" />
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground text-right">
                Max {getMaxPerAgent()} per agent type
              </div>
            </div>
          </div>
        )}

        <p className="text-sm text-muted-foreground leading-relaxed mb-6 line-clamp-2">
          {agent?.description}
        </p>

        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-caption">Tasks Completed</span>
            <span className="font-bold text-foreground">{agent?.tasksCompleted}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-caption">Active Tasks</span>
            <span className="font-bold text-foreground">{agent?.activeTasks}</span>
          </div>
          <div className="w-full h-2.5 bg-muted/50 rounded-full overflow-hidden backdrop-blur-sm">
            <div 
              className="h-full bg-gradient-to-r from-primary to-purple-600 transition-all duration-500 shadow-sm"
              style={{ width: `${agent?.efficiency}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground font-caption">Efficiency</span>
            <span className="font-bold text-primary">{agent?.efficiency}%</span>
          </div>
        </div>

        <div className="space-y-3">
          {agent?.recentActivity && (
            <div className="glass-subtle rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <Icon 
                  name="Clock" 
                  size={16} 
                  color="var(--color-muted-foreground)" 
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground font-semibold mb-1">
                    Recent Activity
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {agent?.recentActivity}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Button
            variant="outline"
            size="default"
            fullWidth
            onClick={handleCardClick}
            iconName="ArrowRight"
            iconPosition="right"
            className="glass-subtle hover:glass-strong hover:border-primary group-hover:shadow-lg transition-all duration-300"
          >
            Open {agent?.name}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgentCard;